import { readHotspotAppearance } from '@/lib/hotspotButton'
import type { Scenario, ScenarioNodeWithDetails, ScenarioWithNodes } from '@/orval/generated/user/model'
import type {
  ButtonActionType,
  ButtonPosition,
  ButtonStyle,
  Hotspot,
  HotspotAction,
  InteractiveButton,
  PlayerConfig,
  ScenarioNode,
  SurveyNodeConfig,
  SurveyQuestion,
  Telop,
} from '@/types/interactiveVideo'
import { buildWideAgenda } from '../wideAgendaConfig'
import { readBranchCueTime } from './branchCueTime'

/**
 * ノードの `config` からワイド型の分岐の問いかけを取り出す（配置＝ノード側が持つ）。
 * 未設定・型違いなら null（選択肢だけを出す）。
 */
export function readBranchQuestionText(config: unknown): string | null {
  if (typeof config !== 'object' || config === null) return null
  const value = (config as Record<string, unknown>).branchQuestionText
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/**
 * 行き先のノードが存在しない遷移ボタンか。
 *
 * 行き先の動画を消すと、ボタンだけが残ることがある（キャンバスには線が出ないので気づけない）。
 * 押すと存在しない動画へ進もうとして画面が真っ暗になるため、公開 config ではバックエンドが
 * 取り除いている（moovy-saas-tsoa `commons/scenarioButtons.ts`）。管理画面のプレビューは
 * 管理用 API のデータから組み立てるので、ここでも同じ基準で取り除いて script とそろえる。
 */
export function isDanglingTransitionButton(
  button: { actionType: string; actionConfig: unknown },
  existingNodeKeys: ReadonlySet<string>,
): boolean {
  if (button.actionType !== 'VIDEO_TRANSITION') return false
  const config = button.actionConfig
  if (typeof config !== 'object' || config === null) return false
  const target = (config as Record<string, unknown>).targetNodeKey
  return typeof target === 'string' && target !== '' && !existingNodeKeys.has(target)
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

function readHotspotAction(actionType: unknown, actionConfig: unknown): HotspotAction | null {
  const config =
    typeof actionConfig === 'object' && actionConfig !== null ? (actionConfig as Record<string, unknown>) : {}
  if (actionType === 'SEEK') {
    return isFiniteNumber(config.time) ? { type: 'seek', time: config.time } : null
  }
  if (actionType === 'VIDEO_TRANSITION') {
    if (typeof config.targetNodeKey !== 'string' || config.targetNodeKey === '') return null
    return {
      type: 'video_transition',
      targetNodeId: config.targetNodeKey,
      startTime: isFiniteNumber(config.startTime) ? config.startTime : 0,
    }
  }
  if (actionType === 'EXTERNAL_LINK') {
    if (typeof config.url !== 'string' || config.url === '') return null
    return {
      type: 'external_link',
      url: config.url,
      openInNewTab: typeof config.openInNewTab === 'boolean' ? config.openInNewTab : true,
    }
  }
  return null
}

/**
 * ノードのクリック領域（ホットスポット・ADR-0022）をプレイヤー用に整える。
 * 保存時にサーバが検証しているが、Json カラムなので壊れた要素は黙って落とす
 * （クリック領域が1つ出ないことより、プレイヤーが落ちる方が悪い）。並び順は保つ。
 */
export function convertHotspots(hotspots: unknown): Hotspot[] {
  if (!Array.isArray(hotspots)) return []

  return hotspots.flatMap((raw): Hotspot[] => {
    if (typeof raw !== 'object' || raw === null) return []
    const { id, label, startTime, endTime, rect, actionType, actionConfig, appearance } = raw as Record<string, unknown>
    if (typeof id !== 'string' || typeof label !== 'string') return []
    if (!isFiniteNumber(startTime) || !isFiniteNumber(endTime) || endTime <= startTime) return []
    if (typeof rect !== 'object' || rect === null) return []
    const { x, y, w, h } = rect as Record<string, unknown>
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(w) || !isFiniteNumber(h)) return []
    if (w <= 0 || h <= 0) return []
    const action = readHotspotAction(actionType, actionConfig)
    if (!action) return []
    return [
      { id, label, startTime, endTime, rect: { x, y, w, h }, action, appearance: readHotspotAppearance(appearance) },
    ]
  })
}

/**
 * 公開/管理DTOのアンケート設問（options 込み）をプレイヤー描画用の単純化型へ変換する。
 * cueTime（提示時刻・秒 / null=末尾）と required、設問型、選択肢を保持する。
 *
 * FREE（自由回答）とリード（PII）項目（leadFieldKind 非null）は選択肢を持たないので描画対象に
 * 含める。SINGLE/MULTI は選択肢が無いと視聴側で回答できない（必須だと閉じるボタンも出ず操作
 * 不能になる）ため除外する。
 */
export function convertSurveyQuestions(
  surveyQuestions: ScenarioNodeWithDetails['surveyQuestions'] | undefined,
): SurveyQuestion[] {
  return (surveyQuestions ?? [])
    .filter(
      (question) =>
        question.leadFieldKind != null || question.questionType === 'FREE' || (question.options?.length ?? 0) > 0,
    )
    .map((question) => ({
      id: question.id,
      text: question.text,
      questionType: question.questionType,
      required: question.required,
      leadFieldKind: question.leadFieldKind ?? null,
      cueTime: question.cueTime ?? null,
      order: question.order,
      options: (question.options ?? []).map((option) => ({
        id: option.id,
        label: option.label,
      })),
    }))
}

/**
 * SURVEYノード（POPUP 全画面ゲート・#263）をプレイヤー描画用の ScenarioNode に変換する。
 * 動画を持たないため videoUrl/buttons は空で、設問（options 込み）と送信後の遷移先
 * （config.defaultTargetNodeKey ＋ ルーティング設問による分岐マップ・#265）を載せる。
 * プレイヤーはこれを食ってゲートを描画し、送信時に回答から遷移先を解決する。
 */
function buildSurveyGateNode(node: ScenarioNodeWithDetails): ScenarioNode {
  const config = (node.config as SurveyNodeConfig | null) ?? {}
  return {
    id: node.nodeKey,
    nodeKey: node.nodeKey,
    type: 'SURVEY',
    videoUrl: '',
    title: node.title,
    buttons: [],
    telops: [],
    surveyQuestions: convertSurveyQuestions(node.surveyQuestions),
    defaultTargetNodeKey: config.defaultTargetNodeKey ?? null,
    routingQuestionId: config.routingQuestionId ?? null,
    routing: config.routing ?? null,
  }
}

/**
 * ScenarioWithNodes型は生成されたコードではnodes: ScenarioNode[]となっているが、
 * 実際のAPIレスポンスはnodes: ScenarioNodeWithDetails[]を返す
 */
type ScenarioWithNodeDetails = Omit<Scenario, 'nodes'> & {
  nodes: ScenarioNodeWithDetails[]
  initialNodeKey: string
}

/**
 * RecordStringUnknownをButtonPositionに変換
 */
function convertToButtonPosition(data: Record<string, unknown> | null | undefined): ButtonPosition | undefined {
  if (!data) return undefined

  const position: ButtonPosition = {}

  if (typeof data.x === 'number') position.x = data.x
  if (typeof data.y === 'number') position.y = data.y
  if (data.alignment === 'left' || data.alignment === 'center' || data.alignment === 'right') {
    position.alignment = data.alignment
  }

  // 何かしらのプロパティがある場合のみ返す
  return Object.keys(position).length > 0 ? position : undefined
}

/**
 * RecordStringUnknownをButtonStyleに変換
 */
function convertToButtonStyle(data: Record<string, unknown> | null | undefined): ButtonStyle | undefined {
  if (!data) return undefined

  const style: ButtonStyle = {}

  if (typeof data.backgroundColor === 'string') style.backgroundColor = data.backgroundColor
  if (typeof data.textColor === 'string') style.textColor = data.textColor
  if (typeof data.borderRadius === 'string') style.borderRadius = data.borderRadius
  if (typeof data.fontSize === 'string') style.fontSize = data.fontSize

  // 何かしらのプロパティがある場合のみ返す
  return Object.keys(style).length > 0 ? style : undefined
}

/**
 * ノードのボタンをプレイヤー形式に変換する。行き先の動画が消えた分岐ボタンは除く。
 * 非同期版・同期版の両方の変換で使う。
 */
function convertButtons(
  node: ScenarioNodeWithDetails,
  nodes: readonly ScenarioNodeWithDetails[],
  existingNodeKeys: ReadonlySet<string>,
): InteractiveButton[] {
  const liveButtons = (node.buttons || []).filter((button) => !isDanglingTransitionButton(button, existingNodeKeys))
  return liveButtons.map((button) => {
    // actionTypeを変換（データベースのenumとプレイヤーのenumが異なる場合）
    const buttonType: ButtonActionType =
      button.actionType === 'VIDEO_TRANSITION'
        ? ('video_transition' as ButtonActionType)
        : ('external_link' as ButtonActionType)

    // actionConfigをパース
    const actionConfig = button.actionConfig as Record<string, unknown>

    // アクションを構築
    let action:
      | { type: 'video_transition'; targetNodeId: string }
      | { type: 'external_link'; url: string; openInNewTab: boolean }

    // ラベル（デフォルトは元のラベル）
    let label = button.label

    if (button.actionType === 'VIDEO_TRANSITION') {
      const targetNodeKey = (actionConfig.targetNodeKey as string) || ''
      action = {
        type: 'video_transition',
        targetNodeId: targetNodeKey,
      }

      // ラベル未設定のときだけ遷移先ノードのタイトルで補う。
      // 分岐の選択肢は作者が書いた文言（「① ピーク時にスタッフが足りない！」等）が主役で、
      // 遷移先の動画名とは別物なので、入力済みのラベルは上書きしない（ADR-0016）。
      if (!label) {
        const targetNode = nodes.find((n) => n.nodeKey === targetNodeKey)
        label = targetNode?.title ?? label
      }
    } else {
      action = {
        type: 'external_link',
        url: (actionConfig.url as string) || '',
        openInNewTab: (actionConfig.openInNewTab as boolean) ?? true,
      }
    }

    return {
      id: button.id,
      label,
      type: buttonType,
      action,
      position: convertToButtonPosition(button.position),
      style: convertToButtonStyle(button.style),
    }
  })
}

/**
 * シナリオデータをインタラクティブプレイヤー用の形式に変換
 */
export async function convertScenarioToPlayerConfig(
  scenario: ScenarioWithNodes | ScenarioWithNodeDetails,
  getVideoPresignedUrl: (videoId: string) => Promise<string>,
): Promise<PlayerConfig> {
  // ノードを Map に変換
  const nodesMap = new Map<string, ScenarioNode>()

  // 各ノードを処理（実際のAPIレスポンスはScenarioNodeWithDetails[]を返す）
  const nodes = (scenario.nodes || []) as ScenarioNodeWithDetails[]
  const existingNodeKeys = new Set(nodes.map((n) => n.nodeKey))
  for (const node of nodes) {
    // SURVEYノード（全画面ゲート・#263）は動画を持たず、設問と遷移先だけを載せる。
    if (node.type === 'SURVEY') {
      nodesMap.set(node.nodeKey, buildSurveyGateNode(node))
      continue
    }

    // LINKノードなど、videoIdがないノードはスキップ
    if (!node.videoId) {
      continue
    }

    try {
      // 動画の署名付きURLを取得
      const videoUrl = await getVideoPresignedUrl(node.videoId)

      // ボタンをプレイヤー形式に変換
      const buttons = convertButtons(node, nodes, existingNodeKeys)

      // テロップをプレイヤー形式に変換
      const telops: Telop[] = (node.telops || []).map((telop) => ({
        text: telop.text,
        startTime: telop.startTime,
        endTime: telop.endTime,
      }))

      // ScenarioNodeを作成
      const scenarioNode: ScenarioNode = {
        id: node.nodeKey,
        nodeKey: node.nodeKey,
        videoUrl,
        title: node.title,
        buttons,
        telops,
        surveyQuestions: convertSurveyQuestions(node.surveyQuestions),
        branchQuestionText: readBranchQuestionText(node.config),
        branchCueTime: readBranchCueTime(node.config),
        ctaUrl: node.video?.ctaUrl ?? null,
        ctaLabel: node.video?.ctaLabel ?? null,
        hotspots: convertHotspots(node.hotspots),
      }

      nodesMap.set(node.nodeKey, scenarioNode)
    } catch (error) {
      console.error(`[convertScenarioToPlayerConfig] ノード "${node.nodeKey}" の処理中にエラーが発生しました:`, error)
      // エラーが発生したノードはスキップして続行
      continue
    }
  }

  // PlayerConfigを返す
  return {
    initialNodeId: scenario.initialNodeKey,
    nodes: nodesMap,
    autoplay: true,
    muted: true, // 自動再生のため必須
    showProgress: true,
    analyticsEnabled: false, // プレビューではアナリティクスを無効化
    agenda: scenario.displayType === 'WIDE' ? buildWideAgenda(scenario.config, nodesMap) : undefined,
  }
}

/**
 * シナリオデータをプレイヤー形式に変換（動画URLを直接指定）
 * テスト用またはすでにURLが取得済みの場合に使用
 */
export function convertScenarioToPlayerConfigSync(
  scenario: ScenarioWithNodes | ScenarioWithNodeDetails,
  videoUrlMap: Map<string, string>,
  hlsUrlMap?: Map<string, string>,
): PlayerConfig {
  // ノードを Map に変換
  const nodesMap = new Map<string, ScenarioNode>()

  // 各ノードを処理（実際のAPIレスポンスはScenarioNodeWithDetails[]を返す）
  const nodes = (scenario.nodes || []) as ScenarioNodeWithDetails[]
  const existingNodeKeys = new Set(nodes.map((n) => n.nodeKey))
  for (const node of nodes) {
    // SURVEYノード（全画面ゲート・#263）は動画を持たず、設問と遷移先だけを載せる。
    if (node.type === 'SURVEY') {
      nodesMap.set(node.nodeKey, buildSurveyGateNode(node))
      continue
    }

    // LINKノードなど、videoIdがないノードはスキップ
    if (!node.videoId) {
      continue
    }

    // 動画URLを取得
    const videoUrl = videoUrlMap.get(node.videoId)

    if (!videoUrl) {
      continue
    }

    // ボタンをプレイヤー形式に変換
    const buttons = convertButtons(node, nodes, existingNodeKeys)

    // テロップをプレイヤー形式に変換
    const telops: Telop[] = (node.telops || []).map((telop) => ({
      text: telop.text,
      startTime: telop.startTime,
      endTime: telop.endTime,
    }))

    // ScenarioNodeを作成
    const scenarioNode: ScenarioNode = {
      id: node.nodeKey,
      nodeKey: node.nodeKey,
      videoUrl,
      hlsUrl: hlsUrlMap?.get(node.videoId) ?? null,
      title: node.title,
      buttons,
      telops,
      surveyQuestions: convertSurveyQuestions(node.surveyQuestions),
      branchQuestionText: readBranchQuestionText(node.config),
      branchCueTime: readBranchCueTime(node.config),
      hotspots: convertHotspots(node.hotspots),
    }

    nodesMap.set(node.nodeKey, scenarioNode)
  }

  // PlayerConfigを返す
  return {
    initialNodeId: scenario.initialNodeKey,
    nodes: nodesMap,
    autoplay: true,
    muted: true,
    showProgress: true,
    analyticsEnabled: false,
    agenda: scenario.displayType === 'WIDE' ? buildWideAgenda(scenario.config, nodesMap) : undefined,
  }
}
